/**
 * The Venues page header (BON-538).
 *
 * Title, current scope and "Add venue", on the shared `DashboardPageHeader` —
 * the same chrome Overview wears, so the two pages cannot drift apart on the
 * one thing a viewer sees on both.
 *
 * ⚠️ The actions slot deviates from the dashboards pattern doc's controls-free
 * header. The design puts the scope picker and the add button there because
 * both act on the PAGE — the roster and the map answer to the same region
 * filter — and a control governing two cards cannot live inside one of them.
 * The pattern doc needs a line recording the exception.
 */
import { Plus } from 'lucide-react';

import { DashboardPageHeader } from '../../primitives/DashboardPageHeader';
import { NO_REGIONS_NOTE, ScopeNote } from '../../primitives/scope/ScopeNote';

import { VenuesScopePicker } from './VenuesScopePicker';

export interface VenuesHeaderProps {
  /** What the current scope is called. */
  label: string;
  /** The region currently being viewed; null at all-regions scope. */
  currentRegionId: string | null;
  isPhone: boolean;
  /** The viewer is looking at the union of their own regions. */
  isOwnRegions: boolean;
  /** The viewer has no assigned region, so they are shown every region. */
  noRegions: boolean;
  onAddVenue: () => void;
}

export function VenuesHeader({
  label,
  currentRegionId,
  isPhone,
  isOwnRegions,
  noRegions,
  onAddVenue,
}: VenuesHeaderProps) {
  return (
    <DashboardPageHeader
      title="Venues"
      isPhone={isPhone}
      below={noRegions ? <ScopeNote>{NO_REGIONS_NOTE}</ScopeNote> : undefined}
      scope={
        <VenuesScopePicker
          label={label}
          currentRegionId={currentRegionId}
          isPhone={isPhone}
          isOwnRegions={isOwnRegions}
        />
      }
      actions={
        // On a phone the add action is a floating button over the roster
        // instead — the header has no room for it beside a scope pill.
        isPhone ? undefined : (
          <button type="button" className="nb-btn nb-btn-primary" onClick={onAddVenue}>
            <Plus size={16} aria-hidden="true" />
            Add venue
          </button>
        )
      }
    />
  );
}
